import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, CircularProgress } from '@mui/material';
import StyledBgContainer from '../../../components/StyledBgContainer/StyledBgContainer';
import TopNav from '../../../components/TopNav/TopNav';
import StyledButton from '../../../components/Button/Button';
import { getStage } from '../../../services/NetworkServices';
import {
  storeApprovalStatus,
  storeStageNumber,
  removeToken,
  removeEmail,
  removeStageNumber,
  removeApprovalStatus,
} from '../../../utils/auth-token-utils/cookie';
import { SUPERVISOR_SESSION_MARKER } from '../../../hooks/useSupervisorAutoLogout';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';

const WaitingForApproval: React.FC = () => {
  const [checking, setChecking] = useState<boolean>(false);
  const [rejected, setRejected] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleCheckStatus = async (): Promise<void> => {
    setChecking(true);
    try {
      const response = await getStage();
      const data = response.data;
      console.log('[WaitingForApproval] getStage raw response:', data);
      if (data?.data) {
        const { stage, approvalStatus } = data.data;
        console.log('[WaitingForApproval] stage:', stage, '| approvalStatus:', approvalStatus);
        storeStageNumber(stage);
        storeApprovalStatus(approvalStatus);
        console.log('[WaitingForApproval] stored approvalStatus:', localStorage.getItem('approvalStatus'));

        if (approvalStatus === 'approved') {
          console.log('[WaitingForApproval] → approved, navigating');
          if (stage === '1') {
            navigate('/babysitter/createProfile');
          } else {
            navigate('/supervisor/dashboard');
          }
          return;
        }

        if (approvalStatus === 'rejected') {
          console.log('[WaitingForApproval] → rejected, showing rejection UI');
          setRejected(true);
        }
      } else {
        console.warn('[WaitingForApproval] getStage returned no data:', data);
      }
    } catch (err) {
      console.error('[WaitingForApproval] getStage error:', err);
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = (): void => {
    removeToken();
    removeEmail();
    removeStageNumber();
    removeApprovalStatus();
    sessionStorage.removeItem(SUPERVISOR_SESSION_MARKER);
    localStorage.setItem('isLoggedOut', Date.now().toString());
    navigate('/signin');
  };

  return (
    <StyledBgContainer>
      <TopNav />
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            minHeight: '60vh',
            px: { sm: 6 },
          }}
        >
          <Box
            sx={{
              background: rejected
                ? 'rgba(253, 12, 4, 0.15)'
                : 'rgba(251, 80, 18, 0.15)',
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100px',
              height: '100px',
              mb: 4,
            }}
          >
            {rejected ? (
              <BlockIcon sx={{ fontSize: '50px', color: 'rgb(253, 12, 4)' }} />
            ) : (
              <HourglassEmptyIcon
                sx={{ fontSize: '50px', color: 'rgba(251, 80, 18, 1)' }}
              />
            )}
          </Box>

          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontFamily: 'Opensans',
              fontWeight: '700',
              mb: 2,
            }}
          >
            {rejected
              ? 'Account Not Approved'
              : 'Waiting for Account Approval'}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'grey',
              mb: 5,
              maxWidth: '400px',
              lineHeight: 1.6,
            }}
          >
            {rejected
              ? 'Your account has been reviewed and was not approved. Please contact support for more information.'
              : 'Your account is currently under review. An administrator will approve your account shortly. Please check back later.'}
          </Typography>

          {!rejected && (
            <StyledButton
              onClick={handleCheckStatus}
              disabled={checking}
              sx={{ mb: 2, width: '220px' }}
            >
              {checking ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                'Check Status'
              )}
            </StyledButton>
          )}

          <StyledButton
            onClick={handleLogout}
            sx={{
              backgroundColor: 'transparent',
              border: '2px solid rgba(251, 80, 18, 1)',
              '&:hover': {
                backgroundColor: 'rgba(251, 80, 18, 0.1)',
              },
              width: '220px',
            }}
          >
            Sign Out
          </StyledButton>
        </Box>
      </Container>
    </StyledBgContainer>
  );
};

export { WaitingForApproval };
